import React from "react";
import { Modal, Badge } from "react-bootstrap";
import { Download, Eye, Pencil, Trash2 } from "lucide-react";
import { Button } from "./ui";
import { cn } from "../utils/cn";
import "../assets/css/record-detail-modal.css";

export type RecordDetailField = {
  label: string;
  value?: React.ReactNode;
  fullWidth?: boolean;
};

export type RecordDetailGroup = {
  title: string;
  fields?: RecordDetailField[];
  /** Free-form nested content (lists, nested cards, etc.) */
  content?: React.ReactNode;
};

export type RecordDetailActionType =
  | "edit"
  | "delete"
  | "review"
  | "preview"
  | "download"
  | "custom";

export type RecordDetailAction = {
  type: RecordDetailActionType;
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  /** For type "custom" — rendered as-is if provided */
  render?: React.ReactNode;
  /** Wider footer chip for longer labels (e.g. framework generate) */
  wide?: boolean;
};

export type RecordDetailModalProps = {
  show: boolean;
  onHide: () => void;
  title: string;
  subtitle?: string;
  /** Flat field grid (used when groups is empty) */
  fields?: RecordDetailField[];
  /** Named sections for larger / nested records */
  groups?: RecordDetailGroup[];
  /** Extra body content below fields/groups */
  children?: React.ReactNode;
  actions?: RecordDetailAction[];
  size?: "sm" | "lg" | "xl";
  /** Extra class on Bootstrap modal dialog (width/layout variants) */
  dialogClassName?: string;
  /** Extra class on modal body */
  bodyClassName?: string;
  /**
   * `two-row`: Close + secondary actions on row 1; Edit + Delete on row 2 (wide footers).
   */
  footerLayout?: "inline" | "two-row";
};

function displayValue(value: React.ReactNode): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="rdm-empty">—</span>;
  }
  if (typeof value === "boolean") {
    return (
      <Badge bg={value ? "success" : "secondary"} className="rdm-status-badge">
        {value ? "Active" : "Inactive"}
      </Badge>
    );
  }
  return value;
}

function footerBtnClass(wide?: boolean) {
  return wide ? "rdm-footer-btn rdm-footer-btn--wide" : "rdm-footer-btn";
}

function ActionButton({ action }: { action: RecordDetailAction }) {
  if (action.type === "custom" && action.render != null) {
    return <>{action.render}</>;
  }

  const label =
    action.label ||
    ({
      edit: "Edit",
      delete: "Delete",
      review: "Review",
      preview: "Preview",
      download: "Download",
      custom: "Action",
    }[action.type] as string);

  if (action.type === "edit" || action.type === "review") {
    return (
      <Button
        variant="primary"
        className={footerBtnClass(action.wide)}
        onClick={action.onClick}
        disabled={action.disabled}
      >
        <Pencil size={16} aria-hidden />
        {label}
      </Button>
    );
  }

  if (action.type === "delete") {
    return (
      <Button
        variant="danger"
        className={footerBtnClass(action.wide)}
        onClick={action.onClick}
        disabled={action.disabled}
      >
        <Trash2 size={16} aria-hidden />
        {label}
      </Button>
    );
  }

  if (action.type === "preview") {
    return (
      <Button
        variant="outline"
        className={footerBtnClass(action.wide)}
        onClick={action.onClick}
        disabled={action.disabled}
      >
        <Eye size={16} aria-hidden />
        {label}
      </Button>
    );
  }

  if (action.type === "download") {
    return (
      <Button
        variant="primary"
        className={footerBtnClass(action.wide)}
        onClick={action.onClick}
        disabled={action.disabled}
      >
        <Download size={16} aria-hidden />
        {label}
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      className={footerBtnClass(action.wide)}
      onClick={action.onClick}
      disabled={action.disabled}
    >
      {label}
    </Button>
  );
}

function FieldGrid({ fields }: { fields: RecordDetailField[] }) {
  if (!fields.length) return null;
  return (
    <div className="rdm-field-grid">
      {fields.map((field, idx) => (
        <div
          key={`${field.label}-${idx}`}
          className={`rdm-field${field.fullWidth ? " rdm-field--full" : ""}`}
        >
          <div className="rdm-field-label">{field.label}</div>
          <div className="rdm-field-value">{displayValue(field.value)}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Read-only record detail modal for list/card row clicks.
 * Footer actions should call the same handlers as the list (Edit/Delete/Review/Preview/Download).
 */
const RecordDetailModal: React.FC<RecordDetailModalProps> = ({
  show,
  onHide,
  title,
  subtitle,
  fields = [],
  groups = [],
  children,
  actions = [],
  size = "lg",
  dialogClassName,
  bodyClassName,
  footerLayout = "inline",
}) => {
  const secondaryActions = actions.filter(
    (a) => a.type !== "edit" && a.type !== "delete"
  );
  const recordActions = actions.filter(
    (a) => a.type === "edit" || a.type === "delete"
  );

  const closeButton = (
    <Button variant="outline" onClick={onHide} className="rdm-footer-btn">
      Close
    </Button>
  );

  const footerInline = (
    <>
      {closeButton}
      {actions.map((action, idx) => (
        <ActionButton key={`${action.type}-${idx}`} action={action} />
      ))}
    </>
  );

  const footerTwoRow = (
    <>
      <div className="rdm-footer__row rdm-footer__row--secondary">
        {closeButton}
        {secondaryActions.map((action, idx) => (
          <ActionButton key={`${action.type}-${idx}`} action={action} />
        ))}
      </div>
      {recordActions.length > 0 ? (
        <div className="rdm-footer__row rdm-footer__row--record">
          {recordActions.map((action, idx) => (
            <ActionButton key={`${action.type}-${idx}`} action={action} />
          ))}
        </div>
      ) : null}
    </>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      centered
      scrollable
      dialogClassName={cn("rounded-3 record-detail-modal-dialog", dialogClassName)}
      contentClassName="record-detail-modal-content"
    >
      <Modal.Header closeButton className="dashboard-modal-header rdm-header">
        <div className="rdm-header-text">
          <Modal.Title className="rdm-title">{title}</Modal.Title>
          {subtitle ? <div className="rdm-subtitle">{subtitle}</div> : null}
        </div>
      </Modal.Header>

      <Modal.Body className={cn("rdm-body", bodyClassName)}>
        {fields.length > 0 && <FieldGrid fields={fields} />}

        {groups.map((group, idx) => (
          <div key={`${group.title}-${idx}`} className="rdm-group">
            <h6 className="rdm-group-title">{group.title}</h6>
            {group.fields?.length ? <FieldGrid fields={group.fields} /> : null}
            {group.content ? (
              <div className="rdm-group-content">{group.content}</div>
            ) : null}
          </div>
        ))}

        {children}
      </Modal.Body>

      <Modal.Footer
        className={
          footerLayout === "two-row" ? "rdm-footer rdm-footer--two-row" : "rdm-footer"
        }
      >
        {footerLayout === "two-row" ? footerTwoRow : footerInline}
      </Modal.Footer>
    </Modal>
  );
};

export default RecordDetailModal;
